import { SCROLL_TO_PARAMETER } from '@fullstory/routes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Offest from the top of the page, this is roughly the nav + 16px padding
const TOP_OFFSET = 96;
const SCROLL_ATTEMPTS = 10;

/**
 * Helper function to append the scrollTo param to the url. If the id is
 * not provided, then just the url is returned
 * @param url url to append the scrollTo param
 * @param id the id to use in the scrollTo param
 * @returns an updated url with the scrollTo param append if the id is
 *          a defined value
 */
export const generateScrollToURL = (url: string, id?: string | number) =>
  id ? `${url}?${SCROLL_TO_PARAMETER}=${id}` : url;

/**
 * This hook adds _scrollTo_ functionality to a component. This works by
 * returning a callbackRef
 * @param id the unique id provided in the url
 * @param scrollDelay any delay before scroll is intiated
 * @param loading any loading checks that should be done before scrolling, defaults to false
 * @param container the scroll container, dfaults to window.
 * @param animate if the scroll should be smooth or immediate (auto), defaults to true
 * @param scrollTopOffset an offset from the scroll target, defaults to 96
 * @param maxAttempts
 * @returns a callbackRef to be used on the element (ref={setScrollAnchor})
 */
export const useScrollTo = (
  id: string | number,
  scrollDelay: number = 0,
  loading: boolean = false,
  container: Element | Window = window,
  animate: boolean = true,
  scrollTopOffset: number = TOP_OFFSET,
  maxAttempts: number = SCROLL_ATTEMPTS,
) => {
  const location = useLocation();
  const history = useHistory();
  const didScroll = useRef(false);
  const [scrollAnchor, setScrollAnchor] = useState<HTMLDivElement>();

  const setAnchor = useCallback((anchor: HTMLDivElement) => {
    if (anchor) {
      setScrollAnchor(anchor);
    }
  }, []);

  useEffect(() => {
    didScroll.current = false;
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const scrollTo = queryParams.get(SCROLL_TO_PARAMETER);

    let scrollAttempts = 0;
    /**
     * We want to initiate the scroll if this is the target, we haven't scrolled on
     * the current location.search, we have a scroll anchor, and are no longer loading
     */
    if (scrollTo === id && !didScroll.current && scrollAnchor && !loading) {
      const interval = setInterval(() => {
        const scrollTopPosition =
          scrollAnchor.getBoundingClientRect().top + window.scrollY - scrollTopOffset;

        if (scrollTopPosition > 0) {
          container.scrollTo({
            top: scrollTopPosition,
            behavior: animate ? 'smooth' : 'auto',
          });
          clearInterval(interval);
          didScroll.current = true;

          // remove the query param to stop additional scrolls
          queryParams.delete(SCROLL_TO_PARAMETER);
          history.replace({
            pathname: location.pathname,
            search: queryParams.toString(),
            state: location.state,
          });
        }

        if (scrollAttempts > maxAttempts) {
          clearInterval(interval);
          didScroll.current = true;
          console.warn('Unable to scroll to element');
        }

        scrollAttempts++;
      }, scrollDelay);
      return () => {
        clearInterval(interval);
      };
    }
    return undefined;
  }, [
    location.search,
    location.pathname,
    location.state,
    id,
    scrollAnchor,
    scrollDelay,
    loading,
    scrollTopOffset,
    animate,
    maxAttempts,
    container,
    history,
  ]);

  return setAnchor;
};
